import { RiStickyNoteFill, RiStickyNoteLine } from "@remixicon/react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import type { NoteIndicator } from "./density-row";

/**
 * Значок заметки монеты §6.4 — общий у шапки карточки (`density-cards.tsx`)
 * и заголовка группы вида «по монете» (`density-coin-table.tsx`): оба места
 * называют одну и ту же вещь — есть ли заметка по всей монете, а не по
 * конкретной строке, — и обязаны совпадать подписью буква в букву.
 */

/** Область §9 (1.13), общая у заметки и избранного монеты: они относятся ко всей монете. */
export const COIN_SCOPE = "общее для всех бирж и рынков монеты";

/** Трёхзначная честность §6.4: список заметок ещё не прочитан — не «нет». */
export function noteIndicatorFor(base: string, notedBases: Set<string> | null): NoteIndicator {
  return notedBases === null ? "unknown" : notedBases.has(base) ? "yes" : "no";
}

/**
 * Значок заметки монеты — не кнопка, а пометка §9 (1.21): действие «открыть
 * заметки» доступно на строке (хоткей `n`) и в детальной панели, а здесь
 * только видно, есть ли что читать. `"no"` не рисует ничего: значок не
 * утверждает «нет», пока список не прочитан — он либо называет «есть», либо
 * называет «неизвестно», либо молчит.
 */
export function CoinNoteIndicator({
  base,
  hasNote,
  className,
  iconClassName = "size-3.5",
}: {
  base: string;
  hasNote: NoteIndicator;
  /** Цвет и обёртка значка; без него — прежний вид «По монете». */
  className?: string;
  /** Размер значка; по умолчанию прежний 14 px. */
  iconClassName?: string;
}) {
  if (hasNote === "no") {
    return null;
  }
  const label =
    hasNote === "yes"
      ? `Есть заметки по ${base} — ${COIN_SCOPE}`
      : `Заметки по ${base}: неизвестно — список не прочитан; ${COIN_SCOPE}`;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            role="img"
            aria-label={label}
            className={cn(
              "inline-flex",
              hasNote === "yes" ? "text-foreground" : "text-muted-foreground",
              className,
            )}
          />
        }
      >
        {hasNote === "yes" ? (
          <RiStickyNoteFill className={iconClassName} />
        ) : (
          <RiStickyNoteLine className={iconClassName} />
        )}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
